import type { CompanyAvatarChange } from "@rakazo/contracts";
import { COMPANY_ICON_NAMES, PROFILE_IMAGE_MAX_BYTES } from "@rakazo/contracts";
import { Button } from "@rakazo/ui-web";
import {
  Building2,
  BriefcaseBusiness,
  Globe,
  Landmark,
  Heart,
  Code2,
  Megaphone,
  ShoppingBag,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

const icons = {
  building: Building2,
  briefcase: BriefcaseBusiness,
  globe: Globe,
  landmark: Landmark,
  heart: Heart,
  code: Code2,
  megaphone: Megaphone,
  shopping: ShoppingBag,
};
const labels = {
  building: "Building",
  briefcase: "Briefcase",
  globe: "Globe",
  landmark: "Landmark",
  heart: "Heart",
  code: "Code",
  megaphone: "Megaphone",
  shopping: "Shopping",
};
export function CompanyAvatar({
  value,
  className = "size-6",
}: {
  value?: string | null;
  className?: string;
}) {
  const [failed, setFailed] = useState<string | null>(null);
  if (value?.startsWith("data:image/") && failed !== value)
    return (
      <img
        src={value}
        alt=""
        className={`${className} rounded-lg object-contain`}
        onError={() => setFailed(value)}
      />
    );
  const name = value?.startsWith("icon:") ? value.slice(5) : "building";
  const Icon = icons[name as keyof typeof icons] ?? Building2;
  return <Icon className={className} aria-hidden="true" />;
}
export function CompanyAvatarEditor({
  value,
  disabled,
  onError,
  onSave,
}: {
  value: string | null;
  disabled: boolean;
  onError: (message: string | null) => void;
  onSave: (avatar: CompanyAvatarChange) => Promise<void>;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState<CompanyAvatarChange | null>(null),
    [imagePreview, setImagePreview] = useState<string | null>(null);
  const [reading, setReading] = useState(false);
  const [adjustment, setAdjustment] = useState({ scale: 1, x: 0, y: 0 });
  const request = useRef(0);
  useEffect(() => {
    setPending(null);
    setImagePreview(null);
    setAdjustment({ scale: 1, x: 0, y: 0 });
  }, [value]);
  useEffect(
    () => () => {
      request.current++;
    },
    [],
  );
  const busy = disabled || reading;
  const preview =
    pending?.kind === "icon"
      ? `icon:${pending.icon}`
      : pending?.kind === "none"
        ? null
        : pending?.kind === "image"
          ? imagePreview
          : value;
  function adjust(next: typeof adjustment) {
    const source = pending?.kind === "image" ? imagePreview : value;
    if (!source?.startsWith("data:image/")) return;
    setAdjustment(next);
    setImagePreview(source);
    setPending({ kind: "image", base64: source.slice(source.indexOf(",") + 1), adjustment: next });
    onError(null);
  }
  async function upload(file: File) {
    if (
      file.size > PROFILE_IMAGE_MAX_BYTES ||
      !["image/png", "image/jpeg", "image/webp", "image/gif"].includes(file.type)
    ) {
      onError("Choose a PNG, JPEG, WebP or GIF image up to 5 MB.");
      return;
    }
    const current = ++request.current;
    setReading(true);
    onError(null);
    try {
      const data = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () =>
          typeof reader.result === "string"
            ? resolve(reader.result)
            : reject(new Error("Could not read image"));
        reader.onerror = () => reject(new Error("Could not read image"));
        reader.readAsDataURL(file);
      });
      if (current !== request.current) return;
      setImagePreview(data);
      setAdjustment({ scale: 1, x: 0, y: 0 });
      setPending({ kind: "image", base64: data.slice(data.indexOf(",") + 1) });
    } catch {
      if (current === request.current) onError("Could not read this image. Choose another file.");
    } finally {
      if (current === request.current) setReading(false);
    }
  }
  return (
    <div className="space-y-3 border-b border-border pb-5" aria-label="Company avatar">
      <h3 className="font-medium">Avatar</h3>
      <div className="flex flex-wrap items-center gap-4">
        <div
          className={`flex shrink-0 items-center justify-center overflow-hidden rounded-xl border border-border bg-muted ${preview?.startsWith("data:image/") ? "size-28" : "size-16 p-2"}`}
          aria-label="Company avatar preview"
        >
          <div
            className="size-full"
            style={
              pending?.kind === "image"
                ? {
                    transform: `translate(${adjustment.x * 100}%, ${adjustment.y * 100}%) scale(${adjustment.scale})`,
                  }
                : undefined
            }
          >
            <CompanyAvatar value={preview} className="size-full" />
          </div>
        </div>
        <Button variant="outline" disabled={busy} onClick={() => input.current?.click()}>
          Upload image
        </Button>
        <input
          ref={input}
          type="file"
          className="sr-only"
          aria-label="Upload Company avatar"
          accept="image/png,image/jpeg,image/webp,image/gif"
          disabled={busy}
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (file) void upload(file);
          }}
        />
        <Button
          variant="ghost"
          disabled={busy || (!value && !pending)}
          onClick={() => {
            request.current++;
            setPending({ kind: "none" });
            setImagePreview(null);
            onError(null);
          }}
        >
          Reset to default
        </Button>
      </div>
      {preview?.startsWith("data:image/") && (
        <div className="max-w-sm space-y-3">
          {(
            [
              ["scale", "Size", 0.5, 2],
              ["x", "Horizontal", -0.5, 0.5],
              ["y", "Vertical", -0.5, 0.5],
            ] as const
          ).map(([key, label, min, max]) => (
            <label key={key} className="flex items-center gap-3 text-base">
              <span className="w-24 shrink-0">{label}</span>
              <input
                type="range"
                aria-label={`Avatar ${label.toLowerCase()}`}
                className="min-w-0 flex-1 accent-primary"
                min={min}
                max={max}
                step={0.01}
                value={pending?.kind === "image" ? adjustment[key] : key === "scale" ? 1 : 0}
                disabled={busy}
                onChange={(e) => adjust({ ...adjustment, [key]: Number(e.target.value) })}
              />
              {key === "scale" && (
                <output className="w-12 text-right text-base">
                  {Math.round((pending?.kind === "image" ? adjustment.scale : 1) * 100)}%
                </output>
              )}
            </label>
          ))}
          <Button
            variant="outline"
            disabled={busy}
            onClick={() => adjust({ scale: 1, x: 0, y: 0 })}
          >
            Auto fit
          </Button>
        </div>
      )}
      <div className="flex flex-wrap gap-2" role="group" aria-label="Company icons">
        {COMPANY_ICON_NAMES.map((name) => (
          <button
            type="button"
            key={name}
            aria-label={`Use ${labels[name]} icon`}
            aria-pressed={preview === `icon:${name}` || (!preview && name === "building")}
            disabled={busy}
            onClick={() => {
              setPending({ kind: "icon", icon: name });
              setImagePreview(null);
              onError(null);
            }}
            className={`flex size-11 items-center justify-center rounded-lg border border-border hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 ${preview === `icon:${name}` || (!preview && name === "building") ? "bg-accent" : ""}`}
          >
            <CompanyAvatar value={`icon:${name}`} className="size-5" />
          </button>
        ))}
      </div>
      <Button
        disabled={busy || !pending}
        onClick={() => {
          if (pending) void onSave(pending);
        }}
      >
        {disabled ? "Saving…" : "Save avatar"}
      </Button>
    </div>
  );
}
